import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * StaffCodeModal - Modal for staff authentication before sensitive actions
 * 
 * Usage:
 * <StaffCodeModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   actionType="Edit"
 *   actionDescription="Edit reservation for John Doe"
 *   reservationRef="W123456"
 *   onConfirm={(staffData) => {
 *     // Perform the action
 *     console.log("Confirmed by:", staffData.name);
 *   }}
 * />
 */
const StaffCodeModal = ({ 
  open, 
  onClose, 
  actionType = "Action",
  actionDescription = "",
  reservationRef = null,
  onConfirm 
}) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (code.length < 4) {
      toast.error("Please enter a valid staff code");
      return;
    }

    setLoading(true);

    try {
      // Validate staff code
      const validateResponse = await axios.post(`${API}/staff/validate`, { code });
      const staffData = validateResponse.data.staff;

      // Create audit log
      await axios.post(`${API}/audit/log`, {
        staff_code: code,
        action_type: actionType,
        reservation_ref: reservationRef,
        details: actionDescription
      });

      toast.success(`Confirmed by ${staffData.name}`);
      setCode("");
      
      // Execute the confirmed action first, then close modal
      if (onConfirm) {
        onConfirm(staffData);
      }
      
      onClose();
    } catch (error) {
      console.error("Staff validation error:", error);
      if (error.response?.status === 404) {
        toast.error("Invalid staff code");
      } else {
        toast.error("Failed to validate staff code");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-mono text-xl flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#22C55E]" />
            Staff Authentication Required
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Enter your staff code to confirm this action
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Action Info */}
          <div className="glass-card p-3 rounded-lg">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Action</p>
            <p className="text-sm font-semibold text-[#FAFAFA]">{actionType}</p>
            {actionDescription && (
              <p className="text-xs text-[#A1A1AA] mt-1">{actionDescription}</p>
            )}
          </div>

          {/* Staff Code Input */}
          <div>
            <Label className="text-[#A1A1AA]">Staff Code *</Label>
            <Input
              type="password"
              placeholder="Enter 4-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={4}
              className="bg-[#09090B] border-[#27272A] text-[#FAFAFA] text-center text-2xl tracking-widest font-mono mt-2"
              autoFocus
              disabled={loading}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="border-[#27272A] hover:bg-[#27272A]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || code.length < 4}
              className="bg-[#22C55E] hover:bg-[#22C55E]/90 shadow-[0_0_10px_rgba(34,197,94,0.2)]"
            >
              {loading ? "Verifying..." : "Confirm"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StaffCodeModal;
